import React from 'react';
import { Instagram } from 'lucide-react';
import { teamData } from '../data/mock';

/**
 * Team section — shown on the homepage after About.
 *
 * Image handling:
 *  - If a team member has an `image` URL set, that photo is shown.
 *  - Otherwise an elegant initial avatar (teal circle with white initials)
 *    is rendered as a placeholder. To swap in real photos later, just set
 *    `image: "https://..."` on the corresponding entry in
 *    /app/frontend/src/data/mock.js.
 */
const Team = () => {
  return (
    <section id="team" className="team-section" data-testid="team-section">
      <div className="section-container">
        <div className="section-header">
          <span className="team-eyebrow">The TMD Physio Team</span>
          <h2 className="section-title">Meet the people behind your care</h2>
          <p className="section-subtitle">
            A small, dedicated team committed to making your journey to relief feel personal,
            attentive and stress-free from your very first enquiry.
          </p>
        </div>

        <div className="team-grid">
          {teamData.map((member) => (
            <article
              key={member.id}
              className="team-card"
              data-testid={`team-card-${member.id}`}
            >
              <div className="team-avatar-wrap">
                {member.image ? (
                  <img
                    src={member.image}
                    alt={`${member.name}, ${member.role}`}
                    className="team-avatar-img"
                    style={member.imageObjectPosition ? { objectPosition: member.imageObjectPosition } : undefined}
                    width="132"
                    height="132"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div
                    className="team-avatar-initials"
                    aria-hidden="true"
                    data-testid={`team-avatar-${member.id}`}
                  >
                    {member.initials}
                  </div>
                )}
              </div>
              <h3 className="team-name">{member.name}</h3>
              <p className="team-role">{member.role}</p>
              <p className="team-bio">{member.bio}</p>
              {member.instagram ? (
                <a
                  href={member.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="team-instagram"
                  data-testid={`team-instagram-${member.id}`}
                  aria-label={`Follow ${member.name} on Instagram${member.instagramHandle ? ` (${member.instagramHandle})` : ''} — opens in new tab`}
                >
                  <Instagram size={15} strokeWidth={2.2} />
                  <span>{member.instagramHandle || 'Follow on Instagram'}</span>
                </a>
              ) : null}
              {member.credentials?.length ? (
                <ul className="team-credentials">
                  {member.credentials.map((cred) => (
                    <li key={cred}>{cred}</li>
                  ))}
                </ul>
              ) : null}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Team;
